import style from './EnterAmountPageView.css';
import SenderReceiverBlock from '../SenderReceiverBlock/SenderReceiverBlock';
import AmountInput from '../AmountInput/AmountInput';
import { useEffect, useState } from 'preact/hooks';
import useTranslation from '../../../localization/i18n.context';
import P2PCommission from '../P2PCommission/P2PCommission';
import AvailableAmount from '../AvailableAmount/AvailableAmount';
import Button from '../../shared/Button/Button';
import NavigationUtils from '../../../utils/navigation.utils';
import { EnterAmountType } from '../../../routes/EnterAmountPage/EnterAmountProvider';
import Toast from '../../shared/Toast/Toast';
import { EnterAmountPageViewProvider } from './EnterAmountPageViewProvider';
import { SenderReceiverProvider } from '../SenderReceiverBlock/SenderReceiverProvider';
import { renderIf } from '../../../utils/render.utils';
import InlineMessage from '../../shared/InlineMessage/InlineMessage';
import { UrlEvent } from '../../../sdk/url.event';
import { LanguageCode, LanguageId } from '../../../localization/language';
import { TransferReasons } from './TransferReasons';
import SelectBox from '../../shared/SelectBox/SelectBox';

export default function EnterAmountPageView({
  organizationCode = '',
  className = '',
  commission,
  sender,
  receiver,
  params,
  service,
  defaultAmount = 0,
  availableAmounts,
}) {
  const { t, getCurrency, getOrganization, getLanguageId } = useTranslation();

  const [amount, setAmount] = useState(defaultAmount);
  const [showingToast, setShowingToast] = useState(false);
  const [isAmountInputDisabled, setIsAmountInputDisabled] = useState(false);

  const defaultReason = TransferReasons.find(reason => reason.CODE === 'LIVING_EXPENSES');
  const [selectedReason, setSelectedReason] = useState(defaultReason);

  const data = EnterAmountPageViewProvider(
    t,
    params,
    getCurrency,
    commission?.service?.toUpperCase(),
  );
  const [isKeyboardVisible, setKeyboardVisible] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      // If the window height decreases, we assume the keyboard is visible
      if (window.innerHeight < windowHeight) {
        setKeyboardVisible(true);
      } else {
        setKeyboardVisible(false);
      }
      setWindowHeight(window.innerHeight);
    };

    // Add the resize event listener
    window.addEventListener('resize', handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [windowHeight]);

  //Amount
  function onAmountChange(value) {
    setAmount(value);
    if (params?.min && value < params?.min) {
      setShowingToast(true);
    } else {
      setShowingToast(false);
    }
  }

  function onAmountFocus() {
    if (data?.isAmountInputDisabled) {
      setIsAmountInputDisabled(true);
      setShowingToast(true);
    }
  }

  const currentLanguage = getLanguageId() === LanguageId[LanguageCode.ENGLISH] ? 'ENG' : 'ARB';
  const reasonOptions = TransferReasons.map(reason =>
    currentLanguage === 'ENG' ? reason.DESC_ENG : reason.DESC_ARB
  );

  function onReasonChange(selectedReasonText) {
    const reason = TransferReasons.find(reason =>
      currentLanguage === 'ENG'
        ? reason.DESC_ENG === selectedReasonText
        : reason.DESC_ARB === selectedReasonText
    );
    setSelectedReason(reason);
  }

  function onContinueButtonClick() {
    if (service === EnterAmountType.MICROLOAN) {
      return NavigationUtils.navigate(
        NavigationUtils.Routes.INSTALLMENTS,
        false,
        {
          organizationCode,
          amount,
          sender: sender.uid,
          reasonCode: selectedReason?.CODE,
        },
      );
    }
    return NavigationUtils.navigate(NavigationUtils.Routes.SUMMARY, false, {
      sender: sender.uid,
      receiver: receiver.uid,
      currency: commission.currency,
      amount: amount.toString(),
      type: commission.service,
      merchantId: params?.merchantId,
      organizationCode,
      reasonDescription: selectedReason ?
        (getLanguageId() === LanguageId[LanguageCode.ENGLISH] ?
          selectedReason.DESC_ENG :
          selectedReason.DESC_ARB) :
        '',
    });
  }

  //Button
  function checkButtonDisabled() {
    const amountNumber = Number(amount);

    if (!amount) {
      return true;
    }

    const balanceNumber = Number(sender.balance);

    if (commission?.service === EnterAmountType.MICROLOAN) {
      return (
        amountNumber <= 0 ||
        amountNumber > balanceNumber ||
        (amountNumber < Number(params?.min) ?? false)
      );
    }

    return amountNumber <= 0 || amountNumber > balanceNumber;
  }

  const isButtonDisabled = checkButtonDisabled();

  const senderIcon = SenderReceiverProvider.getSenderIcon(
    commission?.service,
    sender,
  );
  const receiverIcon = SenderReceiverProvider.getReceiverIcon(
    commission?.service,
    receiver,
  );

  const openLink = () => {
    const language =
      getLanguageId() === LanguageId[LanguageCode.ENGLISH] ? 'en' : 'ar';
    UrlEvent.open(
      `https://webview.connect.money/static-pages/terms_${language}.html`,
    );
  };

  const selectedReasonText = selectedReason ?
    (currentLanguage === 'ENG' ? selectedReason.DESC_ENG : selectedReason.DESC_ARB) : '';

  return (
    <div className={`${className} ${style.container}`}>
      {renderIf(
        params?.merchantId,
        <InlineMessage
          className={style.inline}
          textKey={t('Merchant ID in use:')}
          textValue={params?.merchantId}
          hasIcon
          showTooltip
          tooltipText={t(
            'Your Merchant ID is unique and linked to your account. If you need to change it, please contact {card name} by dialing',
            {
              '{card name}': getOrganization(organizationCode),
            },
          )}
        />,
      )}
      <SenderReceiverBlock
        className={style.header}
        sender={sender}
        receiver={receiver}
        senderIcon={senderIcon}
        receiverIcon={receiverIcon}
      />
      <AmountInput
        className={style.input}
        currency={commission?.currency}
        onChange={onAmountChange}
        onFocus={onAmountFocus}
        value={amount}
        type={service}
        disabled={isAmountInputDisabled}
      />
      {service !== EnterAmountType.MICROLOAN && (
        <P2PCommission commission={commission} className={style.commission} />
      )}
      <AvailableAmount
        className={style['available-amount']}
        cardFrom={sender}
        amount={amount}
        availableAmounts={availableAmounts}
        type={service}
      />

      <div className={style.reasonSeparator} />

      {service === EnterAmountType.P2P && (
        <SelectBox
          className={style.reasonSelectBox}
          label={t('Transfer Reason')}
          title={t('Select a reason')}
          placeholder={t('Select a reason')}
          options={reasonOptions}
          value={selectedReasonText}
          onChange={onReasonChange}
          closeOnChange={true}
        />
      )}

      <div className="fixed-cta">
        {data && !isKeyboardVisible && (
          <div className={`${className} ${style.toast}`}>
            <Toast
              message={data.toast?.message}
              type={data.toast?.type}
              textAlign={'left'}
              onCloseClick={() => {
                setShowingToast(false);
              }}
              showing={showingToast}
            />
          </div>
        )}
        {!isKeyboardVisible && (
          <>
            {service === EnterAmountType.MICROLOAN && (
              <p className={style.terms}>
                {t('By continuing, you accept our')}{' '}
                <a
                  onClick={() => {
                    openLink();
                  }}
                  className={style.link}
                >
                  {t('terms and conditions')}
                </a>
                .
              </p>
            )}
            <Button
              fullWidth
              onClick={onContinueButtonClick}
              disabled={isButtonDisabled}
            >
              {t('Continue')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}